import { useEffect, useState } from "react";
import { Box, Spinner, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { CampaignService } from "@/services/CampaignService";
import { ModelService } from "@/services/ModelService";
import { ScoringConfigPanel, INHERITANCE_STATE, scoringConfigFromApi, scoringConfigToApi } from "@/scoringUi";
import { buildScoringLabels } from "@/components/jobOffer/scoringLabels";

/**
 * Fork of jobOffer/InheritedCriteriaSection.jsx for Campaign -- same generic scoring-model
 * contract (GET .../scoring-model returns {criteria: [{criterionKey, name, displayName, scoring,
 * overrideMode, source}]}), just pointed at CampaignService instead of ModelService's
 * recruitment-scoped calls. Scenario-level scoring-model call is unchanged/shared (same
 * CyberJob ScenarioController endpoint regardless of BC).
 *
 * Campaign.LocalCriteria (raw Entity authoring) is intentionally NOT editable here -- that's
 * the same full Intent/Entity editor complexity used in the Scenario builder, out of scope for
 * this client-facing form. This section only shows/overrides scenario-inherited criteria.
 */
const CampaignInheritedCriteriaSection = ({ campaignId, scenarioId, scoringOverrides, onOverridesChange, onModelLoaded }) => {
    const { t } = useTranslation("jobOffer");
    const [model, setModel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const labels = buildScoringLabels(t);

    useEffect(() => {
        if (!campaignId && !scenarioId) {
            setModel(null);
            onModelLoaded?.(null);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        const fetcher = campaignId
            ? CampaignService.getScoringModel(campaignId)
            : ModelService.getScenarioScoringModel(scenarioId);
        fetcher
            .then((data) => {
                setModel(data);
                onModelLoaded?.(data);
            })
            .catch(() => {
                setModel(null);
                onModelLoaded?.(null);
            })
            .finally(() => setIsLoading(false));
    }, [campaignId, scenarioId]);

    if (!campaignId && !scenarioId) return null;
    if (isLoading) return <Spinner size="sm" color="brand.400" />;
    if (!model?.criteria?.length) return null;

    const overridesByKey = Object.fromEntries((scoringOverrides ?? []).map((o) => [o.criterionKey, o]));

    const setOverride = (criterionKey, scoring) => {
        const next = (scoringOverrides ?? []).filter((o) => o.criterionKey !== criterionKey);
        next.push({ criterionKey, scoring: scoringConfigToApi(scoring) });
        onOverridesChange(next);
    };

    return (
        <VStack w="full" align="stretch" gap={3}>
            <Text color="white" fontWeight={700} fontSize="md">Kryteria odziedziczone ze scenariusza</Text>
            {model.criteria.map((c) => {
                const hasOverride = !!overridesByKey[c.criterionKey];
                const inheritanceState = hasOverride
                    ? INHERITANCE_STATE.OVERRIDDEN
                    : c.overrideMode === "Locked"
                        ? INHERITANCE_STATE.LOCKED
                        : INHERITANCE_STATE.OVERRIDABLE;
                const readOnly = inheritanceState !== INHERITANCE_STATE.OVERRIDDEN;
                const effectiveScoring = hasOverride
                    ? scoringConfigFromApi(overridesByKey[c.criterionKey].scoring)
                    : scoringConfigFromApi(c.scoring);

                return (
                    <Box key={c.criterionKey}>
                        <Text color="text.grey" fontSize="sm" mb={1}>{c.displayName || c.name}</Text>
                        <ScoringConfigPanel
                            config={effectiveScoring}
                            onChange={(scoring) => setOverride(c.criterionKey, scoring)}
                            readOnly={readOnly}
                            inheritanceState={inheritanceState}
                            onCustomizeForRecruitment={() => setOverride(c.criterionKey, scoringConfigFromApi(c.scoring))}
                            labels={labels}
                        />
                    </Box>
                );
            })}
        </VStack>
    );
};

export default CampaignInheritedCriteriaSection;
